import AbstractCustomerValueModel from '../abstract_customer_value_model';
import BenefitsChartValue from '../../charts/benefits_chart_value';
import BenefitsWaterfallChart from '../../charts/benefits_waterfall_chart';

class BestpassRoi extends AbstractCustomerValueModel {
  custom_chart_classes() {
    return { BenefitsChartValue, BenefitsWaterfallChart };
  }

  // Impact Variables
  percent_reduction_in_time_managing_transponders() {
    return this.input.percent_reduction_in_time_managing_transponders / 100;
  }

  percent_reduction_in_time_managing_payment() {
    return this.input.percent_reduction_in_time_managing_payment / 100;
  }

  percent_reduction_in_time_managing_violations() {
    return this.input.percent_reduction_in_time_managing_violations / 100;
  }

  percent_reduction_in_violations() {
    return this.input.percent_reduction_in_violations / 100;
  }

  percent_reduction_in_toll_spend() {
    return this.input.percent_reduction_in_toll_spend / 100;
  }

  percent_reduction_in_payment_errors() {
    return this.input.percent_reduction_in_payment_errors / 100;
  }

  percent_improvement_in_operations() {
    return this.input.percent_improvement_in_operations / 100;
  }

  // User Inputs
  fleet_type() {
    return this.input.fleet_type;
  }

  tier() {
    return this.input.tier;
  }

  number_of_trucks_regional() {
    return this.input.number_of_trucks_regional;
  }

  number_of_trucks_national() {
    return this.input.number_of_trucks_national;
  }

  number_of_trailers() {
    return this.input.number_of_trailers;
  }

  more_than_one_authority() {
    return this.input.more_than_one_authority;
  }

  discounts_from_authorities() {
    return this.input.discounts_from_authorities;
  }

  fte_managing_transponders() {
    return this.input.fte_managing_transponders;
  }

  monthly_hours_managing_transponders() {
    return this.input.monthly_hours_managing_transponders;
  }

  fte_managing_payment() {
    return this.input.fte_managing_payment;
  }

  monthly_hours_managing_payment() {
    return this.input.monthly_hours_managing_payment;
  }

  fte_managing_violations() {
    return this.input.fte_managing_violations;
  }

  monthly_hours_managing_violations() {
    return this.input.monthly_hours_managing_violations;
  }

  monthly_spend_on_violations() {
    return this.input.monthly_spend_on_violations;
  }

  monthly_spend_on_tolls() {
    return this.input.monthly_spend_on_tolls;
  }

  // Benchmark Inputs
  burden_rate() {
    return this.input.burden_rate;
  }

  annual_transponder_managing_fte_salary() {
    return this.input.annual_transponder_managing_fte_salary;
  }

  annual_payment_managing_fte_salary() {
    return this.input.annual_payment_managing_fte_salary;
  }

  adoption_rate_year_1() {
    return this.input.adoption_rate_year_1 / 100;
  }

  adoption_rate_year_2() {
    return this.input.adoption_rate_year_2 / 100;
  }

  adoption_rate_year_3() {
    return this.input.adoption_rate_year_3 / 100;
  }

  adoption_rate(year) {
    switch (year) {
      case 1: return this.adoption_rate_year_1();
      case 2: return this.adoption_rate_year_2();
      case 3: return this.adoption_rate_year_3();
      default: return 1.0;
    }
  }

  // Static Inputs
  days_worked_per_year() {
    return 260;
  }

  average_payment_error_rate() {
    return 0.5 / 100;
  }

  // override parent
  cumulative_value_by_value_driver(end_year) {
    const memo = {};
    const parent = this;

    this.value_drivers().forEach((value_driver) => {
      if (value_driver === 'streamline_toll_operations') {
        memo[value_driver] = parent.streamline_toll_operations_for_cumulative_value(parent.ending_year(end_year));
      } else {
        memo[value_driver] = parent.cumulative_value_for_benefit(value_driver, parent.ending_year(end_year));
      }
    });

    return memo;
  }

  streamline_toll_operations_for_cumulative_value(year = 1) {
    return this.cumulative_value_for_benefit('reduce_time_managing_transponders', year)
      + this.cumulative_value_for_benefit('reduce_time_managing_payment', year)
      + this.cumulative_value_for_benefit('reduce_time_managing_violations', year);
  }

  // override parent
  cumulative_growth(benefit, year) {
    const benefits_with_salary_growth = [
      'reduce_time_managing_transponders',
      'reduce_time_managing_payment',
      'reduce_time_managing_violations',
    ];

    if (benefits_with_salary_growth.includes(benefit)) {
      return this.salary_growth_factor(year);
    }

    return super.cumulative_growth(benefit, year);
  }

  salary_growth_factor(year) {
    return 1.03 ** (year - 1);
  }

  // Calculated/Derived Inputs
  fully_burdened_hourly_salary(salary) {
    return salary * this.burden_rate() / this.days_worked_per_year() / 8;
  }

  // Benefit 1
  reduce_time_managing_transponders() {
    return this.fte_managing_transponders() * this.monthly_hours_managing_transponders()
      * this.fully_burdened_hourly_salary(this.annual_transponder_managing_fte_salary()) * 12
    * this.percent_reduction_in_time_managing_transponders();
  }

  // Benefit 2
  reduce_time_managing_payment() {
    if (this.more_than_one_authority() === 'N') { return 0; }

    return this.fte_managing_payment() * this.monthly_hours_managing_payment()
      * this.fully_burdened_hourly_salary(this.annual_payment_managing_fte_salary()) * 12
    * this.percent_reduction_in_time_managing_payment();
  }

  // Benefit 3
  reduce_time_managing_violations() {
    return this.fte_managing_violations() * this.monthly_hours_managing_violations()
      * this.fully_burdened_hourly_salary(this.annual_payment_managing_fte_salary()) * 12
    * this.percent_reduction_in_time_managing_violations();
  }

  // Benefit 4
  reduce_number_of_violations() {
    return this.monthly_spend_on_violations() * 12 * this.percent_reduction_in_violations();
  }

  // Benefit 5
  qualify_for_volume_discounts() {
    if (this.discounts_from_authorities() === 'Y') { return 0; }

    return this.monthly_spend_on_tolls() * 12 * this.percent_reduction_in_toll_spend();
  }

  // Benefit 6
  reduce_payment_errors() {
    return this.monthly_spend_on_tolls() * this.average_payment_error_rate() * 12
      * this.percent_reduction_in_payment_errors();
  }

  // Benefit 7
  improve_operations() {
    return this.monthly_spend_on_tolls() * 12 * this.percent_improvement_in_operations();
  }

  all_benefits() {
    return [
      'reduce_time_managing_transponders',
      'reduce_time_managing_payment',
      'reduce_time_managing_violations',
      'reduce_number_of_violations',
      'qualify_for_volume_discounts',
      'reduce_payment_errors',
      'improve_operations'
    ];
  }

  value_drivers() {
    return [
      'streamline_toll_operations',
      'reduce_costs'
    ];
  }

  streamline_toll_operations() {
    return this.reduce_time_managing_transponders()
      + this.reduce_time_managing_payment()
      + this.reduce_time_managing_violations();
  }

  reduce_costs() {
    return this.reduce_number_of_violations()
      + this.qualify_for_volume_discounts()
      + this.reduce_payment_errors()
      + this.improve_operations();
  }

  regional_fee_per_truck() {
    if (this.fleet_type() === 'trucking') {
      switch (this.tier()) {
        case 'foundation': return 2.5;
        case 'coverage': return 3;
        case 'advanced': return 4.5;
        default: return 2.5;
      }
    }

    switch (this.tier()) {
      case 'foundation': return 1.5;
      case 'coverage': return 2;
      case 'advanced': return 3;
      default: return 1.5;
    }
  }

  national_fee_per_truck() {
    if (this.fleet_type() === 'trucking') {
      switch (this.tier()) {
        case 'foundation': return 3.5;
        case 'coverage': return 4;
        case 'advanced': return 5.5;
        default: return 3.5;
      }
    }

    switch (this.tier()) {
      case 'foundation': return 2.25;
      case 'coverage': return 2.75;
      case 'advanced': return 3.75;
      default: return 2.25;
    }
  }

  annual_price_increase(year) {
    return (1 + (this.input.annual_price_increase / 100)) ** (year - 1);
  }

  annual_service_fees_regional(year = 1) {
    return this.number_of_trucks_regional()
      * this.regional_fee_per_truck()
      * 12
      * this.annual_price_increase(year);
  }

  annual_service_fees_national(year = 1) {
    return this.number_of_trucks_national()
      * this.national_fee_per_truck()
      * 12
      * this.annual_price_increase(year);
  }

  annual_service_fees_trailers(year = 1) {
    return this.number_of_trailers()
      * 0.25
      * this.annual_price_increase(year);
  }

  discount_on_service_fees(year = 1) {
    switch (year) {
      case 1: return this.input.monthly_service_fee_discount_1 / 100;
      case 2: return this.input.monthly_service_fee_discount_2 / 100;
      default: return this.input.monthly_service_fee_discount_3 / 100;
    }
  }

  discount_on_upfront_fees() {
    return this.input.upfront_fee_discount / 100;
  }

  service_fees(year = 1) {
    return this.input.monthly_service_fee * 12 * this.annual_price_increase(year);
  }

  upfront_fees(year = 1) {
    if (year > 1) { return 0; }

    return this.input.upfront_fee;
  }

  total_annual_investment(year = 1) {
    const service_fees = this.service_fees(year);
    const upfront_fees = this.upfront_fees(year);

    return service_fees * (1 - this.discount_on_service_fees(year))
      + upfront_fees * (1 - this.discount_on_upfront_fees());
  }

  streamline_toll_operations_label() {
    return this.input.streamline_toll_operations_label;
  }

  reduce_costs_label() {
    return this.input.reduce_costs_label;
  }
}

export default BestpassRoi;
